// src/pages/admin/CompanyList.js
import React, { useEffect, useState } from "react";
import Grid2 from '@mui/material/Unstable_Grid2';
import { Button } from "@mui/material";
import { Breadcrumb } from "../../../components";
import { CardHeader } from "../../../components/cards";
import { Box, Heading } from "../../../components/elements";
import PageLayout from "../../../layouts/PageLayout";
import { DataGrid } from "@mui/x-data-grid";
import { Modal, ModalClose, Sheet, Typography } from "@mui/joy";
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import AddEditCompanyModal from "./AddEditCompanyModal";

export default function CompanyList() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [callApi, setCallApi] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [warningAction, setWarningAction] = useState(null);

  const fetchCompanies = async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}admin/companies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies(res.data.companies);
    } catch (e) {
      if (e.response?.status === 403) {
        alert("Access denied. Master Admin privileges required.");
        navigate("/");
      } else if (e.response?.status === 401) {
        alert("Session expired");
        localStorage.clear();
        navigate("/login");
      } else {
        alert("Failed to fetch companies");
      }
    }
  };

  const toggleStatus = async (companyId) => {
    try {
      await Axios.patch(
        `${process.env.REACT_APP_BASE_URL}admin/company/${companyId}/toggle-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi((prev) => !prev);
      setOpenWarning(false);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to change status");
    }
  };

  const deleteCompany = async (companyId) => {
    try {
      await Axios.delete(
        `${process.env.REACT_APP_BASE_URL}admin/company/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCallApi((prev) => !prev);
      setOpenWarning(false);
    } catch (e) {
      alert(e.response?.data?.message || "Failed to delete company");
    }
  };

  const handleOpenModal = (company = null) => {
    setSelectedCompany(company);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedCompany(null);
    setOpenModal(false);
  };

  const handleSaveSuccess = () => {
    setCallApi((prev) => !prev);
    handleCloseModal();
  };

  const openWarningModal = (action, company) => {
    setWarningAction(() => action);
    setSelectedCompany(company);
    setOpenWarning(true);
  };

  useEffect(() => {
    fetchCompanies();
  }, [callApi]);

  const columns = [
    { field: "s_no", headerName: "S.no", width: 80 },
    { field: "name", headerName: "Company Name", width: 200 },
    { field: "code", headerName: "Code", width: 150 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600",
            backgroundColor: params.row.active ? "#d4edda" : "#f8d7da",
            color: params.row.active ? "#155724" : "#721c24",
          }}
        >
          {params.row.active ? "Active" : "Inactive"}
        </span>
      ),
    },
    { field: "created_by", headerName: "Created By", width: 180 },
    { field: "created_date", headerName: "Created Date", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const company = companies.find(c => c._id === params.row.id);
        return (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {/* Edit */}
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => handleOpenModal(company)}
              title="Edit Company"
            >
              <i className="fas fa-edit" />
            </Button>

            {/* Toggle Active/Inactive */}
            <Button
              size="small"
              variant="contained"
              color={params.row.active ? "warning" : "success"}
              onClick={() => openWarningModal(() => toggleStatus(params.row.id), company)}
              title={params.row.active ? "Deactivate" : "Activate"}
            >
              <i className={`fas fa-${params.row.active ? "ban" : "check"}`} />
            </Button>

            {/* Delete */}
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={() => openWarningModal(() => deleteCompany(params.row.id), company)}
              title="Delete Company"
            >
              <i className="fas fa-trash" />
            </Button>
          </div>
        );
      },
    },
  ];

  const rows = companies?.map((company, idx) => ({
    id: company._id,
    s_no: idx + 1,
    name: company.name,
    code: company.code,
    active: company.active,
    created_by: company.createdBy?.name || "—",
    created_date: new Date(company.createdAt).toLocaleDateString(),
  })) || [];

  return (
    <PageLayout>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, xl: 12 }}>
          <Box className="mc-card-header">
            <Heading as="h4" className="mc-card-title">Company Management</Heading>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleOpenModal(null)}
            >
              <i className="fas fa-plus me-2" />
              Add Company
            </Button>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, xl: 12 }}>
          <Box className="mc-card">
            <div style={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
              />
            </div>
          </Box>
        </Grid2>
      </Grid2>

      {/* Add/Edit Company Modal */}
      <AddEditCompanyModal
        open={openModal}
        onClose={handleCloseModal}
        company={selectedCompany}
        onSaveSuccess={handleSaveSuccess}
      />

      {/* Warning Modal */}
      <Modal open={openWarning} onClose={() => setOpenWarning(false)}>
        <ModalClose />
        <Sheet
          variant="outlined"
          sx={{
            maxWidth: 500,
            borderRadius: "md",
            p: 3,
            boxShadow: "lg",
          }}
        >
          <Typography
            component="h2"
            level="h4"
            textColor="inherit"
            fontWeight="lg"
            mb={1}
          >
            Confirm Action
          </Typography>
          <Typography mb={2}>
            Are you sure you want to proceed with this action for{" "}
            <strong>{selectedCompany?.name}</strong>?
          </Typography>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={() => setOpenWarning(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (warningAction) warningAction();
              }}
            >
              Confirm
            </Button>
          </div>
        </Sheet>
      </Modal>
    </PageLayout>
  );
}
